import { v4 } from 'uuid';
import * as React from 'react';
import { logger } from '@/utils/logger';
import { env } from '@zopauk/nextjs-env';
import { useCookies } from 'react-cookie';

interface AnonymousProviderProps {
  children: React.ReactNode;
}

interface UUID {
  value?: string;
  _type: 'uuid';
}

const AnonymousStateContext = React.createContext<UUID>({ _type: 'uuid' });

const COOKIE_KEY = 'zid';

function AnonymousProvider({ children }: AnonymousProviderProps) {
  const [cookies, setCookie] = useCookies([COOKIE_KEY]);

  React.useEffect(() => {
    if (cookies[COOKIE_KEY]) {
      return;
    }
    const anonymousUuid = v4();
    setCookie(COOKIE_KEY, anonymousUuid, {
      domain: env('COOKIE_BASE_DOMAIN'),
    });
    logger.debug({
      message: `Set new unique key: ${anonymousUuid}`,
      id: '[AnonymousProvider]: Debug',
      sessionId: anonymousUuid,
    });
  }, [cookies, setCookie]);

  return (
    <AnonymousStateContext.Provider
      value={{
        value: cookies[COOKIE_KEY],
        _type: 'uuid',
      }}
    >
      {children}
    </AnonymousStateContext.Provider>
  );
}

function useAnonymousUuid() {
  const context = React.useContext(AnonymousStateContext);
  if (context === undefined) {
    throw new Error('useAnonymousUUID must be used within a AnonymousProvider');
  }
  return context;
}

export { AnonymousProvider, useAnonymousUuid };
